import { useRef, type KeyboardEvent } from 'react';
import { TAB_PANEL_ID, tabId } from '../filterModel';
import { tabLabels, type DashboardCounts, type DashboardTab } from '../dashboardTypes';

const ORDER: DashboardTab[] = ['All', 'Pending', 'Processing', 'Paid', 'NeedsAttention'];

function countOf(counts: DashboardCounts | null, tab: DashboardTab): number | null {
  if (!counts) return null;
  switch (tab) {
    case 'All': return counts.all;
    case 'Pending': return counts.pending;
    case 'Processing': return counts.processing;
    case 'Paid': return counts.paid;
    default: return counts.needsAttention;
  }
}

/** Status tabs (SRS 15). The counts are the server's. Arrow keys, Home and End move between tabs (roving tabindex). */
export function StatusTabs({ tab, counts, onChange }: { tab: DashboardTab; counts: DashboardCounts | null; onChange: (tab: DashboardTab) => void }) {
  const refs = useRef<Partial<Record<DashboardTab, HTMLButtonElement | null>>>({});

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const index = ORDER.indexOf(tab);
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (index + 1) % ORDER.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + ORDER.length) % ORDER.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = ORDER.length - 1;
    if (next === null) return;
    e.preventDefault();
    const target = ORDER[next]!;
    onChange(target);
    refs.current[target]?.focus();
  }

  return (
    <div className="tpd-tabs" role="tablist" aria-label="Payment status" onKeyDown={onKeyDown}>
      {ORDER.map((t) => {
        const count = countOf(counts, t);
        return (
          <button
            key={t} id={tabId(t)} ref={(el) => { refs.current[t] = el; }} type="button" role="tab" aria-selected={tab === t} aria-controls={TAB_PANEL_ID}
            tabIndex={tab === t ? 0 : -1} className={`tpd-tab${tab === t ? ' is-active' : ''}`} onClick={() => onChange(t)}
          >
            {tabLabels[t]}{' '}
            <span className="tpd-tab__count" data-testid={`tab-count-${t}`}>{count === null ? '—' : count}</span>
          </button>
        );
      })}
    </div>
  );
}
